/**
 * The dates of birth the product accepts: from 1900 on, and at least {@link MIN_AGE} years before today, so minors
 * are never profiled. Plain `YYYY-MM-DD` strings, compared as strings (ISO order is date order). One copy for the
 * form schema, the server's zod schema and the date field's input mask.
 */
export const MIN_BIRTHDATE = '1900-01-01'
export const MIN_AGE = 16

/** The latest date of birth that is {@link MIN_AGE} today, from local parts; a 29 February lands on the 28th. */
export function maxBirthdate(today = new Date()): string {
  return maxBirthdateOn(today.getFullYear(), today.getMonth() + 1, today.getDate())
}

function maxBirthdateOn(todayYear: number, month: number, todayDay: number): string {
  const year = todayYear - MIN_AGE
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  const day = month === 2 && todayDay === 29 && !leap ? 28 : todayDay
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// UTC+14 (Kiribati), the zone furthest ahead.
const LATEST_UTC_OFFSET_MS = 14 * 3_600_000

/**
 * {@link maxBirthdate} on the latest calendar day anywhere. The server cannot know the writer's zone, so it bounds by
 * this one and never refuses a sixteenth birthday a writer's own today has reached; the client bounds by its own day.
 */
export function maxBirthdateAnywhere(now = new Date()): string {
  const ahead = new Date(now.getTime() + LATEST_UTC_OFFSET_MS)
  return maxBirthdateOn(ahead.getUTCFullYear(), ahead.getUTCMonth() + 1, ahead.getUTCDate())
}

export const isBirthdateFromMin = (iso: string): boolean => iso.slice(0, 10) >= MIN_BIRTHDATE

export const isBirthdateOfAge = (iso: string, today?: Date): boolean => iso.slice(0, 10) <= maxBirthdate(today)

/** Same calendar day: a `date` column can come back as a full ISO datetime. */
export const isSameBirthdate = (iso: string, stored: string | null | undefined): boolean =>
  !!stored && iso.slice(0, 10) === stored.slice(0, 10)

/**
 * Whether the server stores this birthdate. The one already on file passes as it is (some were stored before the
 * bounds); a new one must be from 1900 on and {@link MIN_AGE} on the latest calendar day anywhere.
 */
export const isBirthdateWritable = (iso: string, stored: string | null | undefined, now?: Date): boolean =>
  isSameBirthdate(iso, stored) || (isBirthdateFromMin(iso) && iso.slice(0, 10) <= maxBirthdateAnywhere(now))
